import React from 'react'
import qs from 'qs'
import { shape, string, array, arrayOf } from 'prop-types'
import { createComponent, withTheme } from '@ycos/fela'
import { createFontStyle, getFontStyles } from '@ycos/primitives'
import { BackgroundPicture } from '@ycos/picture'
import { getTags } from '@ycos/coremedia-renderer'
import { Analytics } from '@ycos/analytics'

const Container = createComponent(
  'Container',
  ({ theme }) => ({
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    height: '100%',
    textDecoration: 'none',
    justifyContent: 'center',
    'screen-medium': {
      display: 'block',
      padding: `0 ${3 * theme.spacingMultiplier}px`
    }
  }),
  'a'
)

const Wrapper = createComponent('Wrapper', ({ theme, brand }) => ({
  padding: brand?.site === 'theoutnet' ? `0 ${4 * theme.spacingMultiplier}px` : 'initial'
}))

const CategoriesCarouselWrapper = createComponent('CategoriesCarouselWrapper', ({ theme }) => ({
  padding: `${3 * theme.spacingMultiplier}px ${2 * theme.spacingMultiplier}px`,
  'screen-large': {
    padding: `${3 * theme.spacingMultiplier}px 0`,
    color: theme.typography.colors.PrimaryWhite
  },
  'screen-small-max': {
    padding: '24px 0 24px 8px',
    marginRight: '-20px'
  }
}))

const CategoriesWrapper = createComponent('CategoriesWrapper', ({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  overflowX: 'scroll',
  gap: `${2 * theme.spacingMultiplier}px`,
  scrollbarWidth: 'none',
  '::-webkit-scrollbar': {
    display: 'none'
  },
  'screen-medium': {
    justifyContent: 'center',
    overflowX: 'initial'
  }
}))

const Category = createComponent(
  'Category',
  ({ theme, brand }) => {
    const fontStyle = getFontStyles(theme.typography, { type: 'Label', name: '1' })
    return {
      ...fontStyle,
      textDecoration: 'none',
      padding: `${brand === 'theoutnet' ? 9 : 11}px ${2 * theme.spacingMultiplier}px `,
      border: `1px solid ${theme.typography.colors.PrimaryWhite}`,
      borderRadius: '2px',
      color: theme.typography.colors.PrimaryWhite,
      flexShrink: 0,
      ':hover': {
        color: theme.typography.colors.PrimaryBlack,
        background: theme.typography.colors.PrimaryWhite
      }
    }
  },
  'a'
)

const SingleColumn = createComponent('SingleColumn', ({ brand }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: brand?.site === 'theoutnet' ? '100%' : '100px',
  'screen-large': {
    height: '120px'
  }
}))

const CenterSaleTitle = createComponent(
  'CenterSaleTitle',
  ({ theme }) => ({
    color: theme.typography.colors.PrimaryWhite,
    fontSize: '60px',
    textTransform: 'uppercase',
    lineHeight: '60px',
    margin: 0,
    fontWeight: 850,
    textAlign: 'center',
    ...theme.custom.saleBannerCategories.title,
    textShadow: '0px 3px 6px rgba(0, 0, 0, 0.12), 0px 6px 12px rgba(0, 0, 0, 0.20)',
    'screen-large': {
      lineHeight: '80px',
      fontSize: '80px',
      ...theme.custom.saleBannerCategories.title
    },
    'screen-small-max': {
      marginBottom: '8px'
    }
  }),
  'h2'
)

const CenterSaleSubTitle = createFontStyle('CenterSaleSubTitle', { type: 'Paragraph', tagName: 'p', name: '1' }, ({ theme }) => ({
  color: theme.typography.colors.PrimaryWhite,
  lineHeight: '40px',
  textTransform: 'uppercase',
  fontSize: '16px',
  margin: 0,
  fontWeight: 850,
  textAlign: 'center',
  ...theme.custom.saleBannerCategories.preTitle,
  'screen-large': {
    fontSize: '40px',
    ...theme.custom.saleBannerCategories.preTitle
  }
}))

const CenterSaleCTAText = createFontStyle('CenterSaleCTAText', { type: 'Label', tagName: 'p', name: '4' }, ({ theme, brand }) => ({
  color: brand === 'mrp' ? theme.typography.colors.SecondaryGreySteel : theme.typography.colors.PrimaryGrey,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: brand === 'mrp' ? '0.18px' : '0.12px',
  textAlign: 'center',
  fontWeight: 400,
  margin: 0,
  'screen-large': {
    color: theme.typography.colors.PrimaryGrey,
    fontSize: brand === 'mrp' ? '14px' : '12px',
    lineHeight: brand === 'mrp' ? '18px' : '16px',
    letterSpacing: brand === 'mrp' ? '0.18px' : '0.12px'
  }
}))

const getHeightStyles = (tags) => {
  const heightTag = getTags(tags, 'SaleBanner Height')[0]

  const heightMap = {
    medium: '304px',
    large: '380px'
  }

  const heightStyles = {
    height: '260px',
    'screen-large': { height: '292px' },
    'screen-small-max': { height: heightMap[heightTag] }
  }

  return heightStyles
}

/* MRP and NAP Sale Banner component */
/* First item => Sale Banner teaser with target */
/* Second item => Categories carousel */
const SaleBannerWithCategories = (props) => {
  const { item, config = {}, brand = {}, theme, index } = props
  const { preTitlePlain, teaserTitle, pictures, dataCmMetadata, fetchPriority, target } = item?.items?.[0]
  const { imageSphere } = config
  const contentContainerStyles = theme.custom.saleBannerCategories.contentContainerStyles

  return (
    <Wrapper brand={brand}>
      <BackgroundPicture
        fetchPriority={fetchPriority}
        opacity={1}
        images={pictures}
        height="100%"
        opacityColor="transparent"
        additionalStyles={getHeightStyles(item.tags)}
        imagesphere={imageSphere}
        lazy={false}
        contentContainerStyles={contentContainerStyles}
      >
        <Container data-cm-metadata={dataCmMetadata} data-testid="saleBannerLink" href={target.href} onClick={track({ item: item?.items?.[0], index, brand })}>
          <SingleColumn brand={brand}>
            <CenterSaleTitle>{teaserTitle}</CenterSaleTitle>
            <CenterSaleSubTitle brand={brand}>{preTitlePlain}</CenterSaleSubTitle>
          </SingleColumn>
        </Container>
        <CategoriesCarouselWrapper>
          <CategoriesWrapper>
            {item?.items?.[1]?.items?.map((category) => (
              <Category brand={brand} href={category.target?.href} key={`category-${category.teaserTitle}`} onClick={track({ item: category, brand })}>
                {category.teaserTitle}
              </Category>
            ))}
          </CategoriesWrapper>
        </CategoriesCarouselWrapper>
        <CenterSaleCTAText brand={brand}>{target.teaserTextPlain}</CenterSaleCTAText>
      </BackgroundPicture>
    </Wrapper>
  )
}

SaleBannerWithCategories.layoutVariant = ['isc-sale-banner-with-categories']

SaleBannerWithCategories.propTypes = {
  item: shape({
    items: arrayOf(
      shape({
        preTitlePlain: string,
        subTitlePlain: string,
        teaserTitle: string.isRequired,
        teaserTextPlain: string.isRequired,
        pictures: array,
        layoutVariant: string.isRequired,
        target: shape({
          href: string.isRequired,
          teaserTitle: string,
          teaserTextPlain: string.isRequired
        }).isRequired
      })
    )
  })
}

SaleBannerWithCategories.defaultProps = {
  item: {}
}

export default withTheme(SaleBannerWithCategories)

// Tech Debt: Copied from editorial/src/rules/NAPHomepageAnalytics.js for temp fix as the rules wont apply for this component
// Revisit NAPHomepageAnalytics to apply or provide customGA track to non-coremedia-renderer-registered components

const getCampaignEntry = (link) => {
  const queryString = link.substring(link.indexOf('?') + 1)
  const queryParams = qs.parse(queryString)
  const cmspQueryParam = queryParams.cm_sp || ''
  if (!cmspQueryParam) return null

  const cmspSections = cmspQueryParam.split('-_-')
  const placement = cmspSections[0]
  const position = cmspSections[1]
  const campaignName = cmspSections[2]
  const campaignStartDate = cmspSections[3]

  if (!placement || !position || !campaignName || !campaignStartDate) return null
  return {
    placement,
    position,
    campaignName,
    campaignStartDate
  }
}

const getSiteMerchObject = (component) => {
  const { href } = component
  const hrefContainsAnalyticsString = href && href.indexOf('cm_sp=') !== -1
  const campaignInfo = hrefContainsAnalyticsString ? getCampaignEntry(href) : undefined
  if (campaignInfo) {
    return {
      placement: 'homepage',
      position: campaignInfo.position,
      campaignName: campaignInfo.campaignName,
      campaignStartDate: campaignInfo.campaignStartDate,
      modulePosition: `module${component.analyticsMetadata?.moduleNumber}`
    }
  }
}

const track = ({ item, index, brand }) => {
  if (!item.href) return undefined
  const { href } = item
  const hrefContainsAnalyticsString = href && href.indexOf('cm_sp=') !== -1
  if (hrefContainsAnalyticsString) {
    const siteMerchandising = getSiteMerchObject(item, index)
    if (siteMerchandising) {
      const analytics = new Analytics({ brand: brand.brandId })
      return () => {
        analytics.trackEvent({
          event: 'site merchandising - click',
          effect: 'campaign click',
          category: {
            primaryCategory: 'site merchandising',
            subCategory: 'homepage'
          },
          attributes: {
            siteMerchandising
          }
        })
      }
    }
  }
}
